import api from '@common/api';
import { DocumentsModel } from '@common/models/DocumentsModel';
import { InvestorAccountModel } from '@common/models/InvestorAccountModel';
import session from '@common/session';
import { addMonths, formatDate, getMonthNameByMonthNumber, getToday } from '@common/utility/datetime';
import { AgreementCode, TaxDocumentGroup } from '@services/appsync/types';
import { LINKS, ROUTES } from 'src/apps/invest/config';
import create from 'zustand';
import { Agreement, DocumentLink, StatementsByYear, TaxDocument } from '../../types';

interface DocumentsViewModelProps {
  text?: string;
  isLoading: boolean;
  taxDocuments: TaxDocument[];
  agreements: Agreement[];
  signedAgreements: number;
  legalLibrary: DocumentLink[];
  cashFlowDocs: DocumentLink[];
}

interface DocumentsViewModelActions {
  setIsLoading: (isLoading: boolean) => void;
  setText: (text: string) => void;
  getStatementMonths: (startDate: Date) => StatementsByYear;
  getTaxDocumentUrl: (document: TaxDocument) => Promise<string | null | undefined>;
  init: () => Promise<void>;
}
export const useDocumentsViewModel = create<DocumentsViewModelProps & DocumentsViewModelActions>((set) => ({
  agreements: [],
  cashFlowDocs: [],
  getStatementMonths: () => {
    const accountStart = InvestorAccountModel.getState().investorAccount?.created_at;
    if (!accountStart) return {};

    const startMonth = new Date(accountStart);
    const endMonth = new Date(addMonths(getToday(), -1));

    const months: StatementsByYear = {};
    while (startMonth <= endMonth) {
      const year = startMonth.getFullYear();
      const monthNumber = startMonth.getMonth() + 1;

      const label = `${getMonthNameByMonthNumber(monthNumber - 1)} ${year} Account Statement`;
      const issued = formatDate(new Date(year, monthNumber, 0), 'MMMM D, YYYY');
      const month = `${year}-${monthNumber.toString().padStart(2, '0')}`;

      if (!months[year]) months[year] = [];

      months[year].unshift({ issued, label, month });

      startMonth.setMonth(startMonth.getMonth() + 1);
    }

    return months;
  },
  getTaxDocumentUrl: async (document) => {
    const investorID = session.get()?.investorId;
    if (!investorID) return;
    return await api.getTaxDocumentPresignedURL({
      form_code: document.form.form_code,
      investor_account_id: investorID,
      suffix: document.form.suffix ?? '',
      tax_period: document.year,
    });
  },
  init: async () => {
    const documentModel = DocumentsModel.getState();
    const investorModel = InvestorAccountModel.getState();

    let taxDocuments: TaxDocumentGroup[] = [];
    const fetchResult = await documentModel.fetchTaxDocuments();
    if (fetchResult.success && fetchResult.taxDocuments) {
      taxDocuments = fetchResult.taxDocuments.sort((a, b) => b.tax_period - a.tax_period);
    }

    const flattenedTaxDocuments: TaxDocument[] = [];
    taxDocuments.forEach((taxYear) => {
      taxYear.forms?.forEach((form) => {
        flattenedTaxDocuments.push({
          form: form,
          year: taxYear.tax_period,
        });
      });
    });

    const advisoryAgreement = !investorModel.investorAccount?.unaccepted_agreements?.includes(AgreementCode.ADVISORY);
    const touPrivacy = !investorModel.investorAccount?.unaccepted_agreements?.includes(AgreementCode.TOU_PRIVACY);
    const dwollaTouPrivacy = !investorModel.investorAccount?.unaccepted_agreements?.includes(
      AgreementCode.DWOLLA_TOU_PRIVACY,
    );

    let signed = 0;
    if (advisoryAgreement) signed++;
    if (touPrivacy) signed = signed + 2;
    if (dwollaTouPrivacy) signed = signed + 2;

    const agreements: Agreement[] = [
      {
        accepted: advisoryAgreement,
        link: advisoryAgreement ? LINKS.PROGRAM_AGREEMENT : ROUTES.inboxAgreementAlert,
        title: 'Advisory Agreement',
      },
      {
        accepted: touPrivacy,
        link: touPrivacy ? LINKS.TERMS : ROUTES.inboxDwollaAlert,
        title: 'Our Terms of Service',
      },
      {
        accepted: touPrivacy,
        link: touPrivacy ? LINKS.PRIVACY_POLICY : ROUTES.inboxDwollaAlert,
        title: 'Our Privacy Policy',
      },
      {
        accepted: dwollaTouPrivacy,
        link: dwollaTouPrivacy ? LINKS.DWOLLA_TERMS_OF_SERVICE : ROUTES.inboxDwollaAlert,
        title: "Dwolla's Terms of Service",
      },
      {
        accepted: dwollaTouPrivacy,
        link: dwollaTouPrivacy ? LINKS.DWOLLA_PRIVACY_POLICY : ROUTES.inboxDwollaAlert,
        title: "Dwolla's Privacy Policy",
      },
    ];

    const legalLibrary: DocumentLink[] = [
      {
        link: LINKS.ESIGN,
        title: 'E-Sign Consent',
      },
      ... (investorModel.subscriptionOffer?.documents ?? []).filter(x => x.url.endsWith('.pdf')).map(x => ({ link: x.url, title: x.title })),
    ];

    // TODO: We should make this dynamic based on the investor's Cash Flow subscription offer.
    const cashFlowDocs: DocumentLink[] = [
      {
        link: LINKS.CASH_FLOW_2022_CF1,
        title: '2022 Cash Flow (Concreit Fund 1 LLC) Audited Financials',
      },
    ];

    set({ agreements, cashFlowDocs, legalLibrary, signedAgreements: signed, taxDocuments: flattenedTaxDocuments });
  },
  isLoading: true,
  legalLibrary: [],
  setIsLoading: (isLoading: boolean) => set({ isLoading }),
  setText: async (text: string) => {
    set({ text });
  },
  signedAgreements: 0,
  taxDocuments: [],
}));
